import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  addObservationAddendum,
  getMyObservations,
  getObservation,
} from "../../services/observationService";
import {
  DISPLAY_STATUS,
  FINALIZED_STATUSES,
  OPEN_STATUSES,
  displayStatusMeta,
} from "../../utils/constants/observations";
import { awaitsPeerConfirmation } from "../../components/scheduling/conductAction";
import { formatShortDate, toLocalISODate } from "../../utils/helpers/dateDisplay";
import { fullName } from "../../utils/helpers/userDisplay";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import Spinner from "../../components/Spinner/Spinner";

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

const FINALIZED_PARAM = [...FINALIZED_STATUSES].join(",");

const OPEN_PARAM = [...OPEN_STATUSES].join(",");

// Which side of the visit a segment asks about — and, since two of them ask the
// same side, which statuses. `main_observer` is narrower than the API's
// `observer` on purpose: it excludes visits you only booked.
const SEGMENTS = [
  {
    key: "conducted",
    role: "main_observer",
    statuses: FINALIZED_PARAM,
    ordering: "-observed_at",
    labelKey: "myObservations.tabConducted",
    emptyKey: "myObservations.emptyConducted",
    emptyBodyKey: "myObservations.emptyConductedBody",
  },
  {
    key: "observed",
    role: "observed",
    statuses: FINALIZED_PARAM,
    ordering: "-observed_at",
    labelKey: "myObservations.tabObserved",
    emptyKey: "myObservations.emptyObserved",
    emptyBodyKey: "myObservations.emptyObservedBody",
  },
  // The other half of "conducted": same role, opposite end of the visit's life.
  // Somebody put your name on these and nobody has run them yet — before this
  // tab the assignment notification was the only copy a manager-scheduled
  // evaluator ever got, and the work was findable nowhere in the product.
  // Ordered by scheduled_date ASCENDING so the oldest booking — the most
  // overdue — is the first row; `-observed_at` would sort nothing here, every
  // row's observed_at is null by definition.
  {
    key: "assigned",
    role: "main_observer",
    statuses: OPEN_PARAM,
    ordering: "scheduled_date",
    labelKey: "myObservations.tabAssigned",
    emptyKey: "myObservations.emptyAssigned",
    emptyBodyKey: "myObservations.emptyAssignedBody",
  },
];

// One vocabulary for a submitted verdict, shared by the badge and the strip, so
// a question reads the same whether you scan the squares or read the row. Same
// emerald / rose / slate the rest of the product uses for pass / fail / not
// observed — this is presentation of a SNAPSHOT, not a score (those are masked
// server-side for anyone but the teacher and their school's management).
const VERDICT = {
  yes: { badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", labelKey: "common.yes" },
  no: { badge: "bg-rose-100 text-rose-700", dot: "bg-rose-500", labelKey: "common.no" },
  none: { badge: "bg-slate-100 text-slate-500", dot: "bg-slate-200", labelKey: "common.notObserved" },
};

const verdictOf = (answer) => {
  const value = String(answer?.selected_option_value_snapshot ?? "").toLowerCase();
  if (value === "yes") return VERDICT.yes;
  if (value === "no") return VERDICT.no;
  return VERDICT.none;
};

// Standards in rubric order, each carrying the answers that belong to it. The
// server sends `answers` already in rubric order, so first-seen order is the
// right order; `standard_scores` only supplies the titles and the percentage.
const groupAnswers = (detail) => {
  const meta = new Map(
    (detail?.standard_scores || []).map((s) => [s.standard, s]),
  );
  const groups = new Map();
  for (const answer of detail?.answers || []) {
    const key = answer.standard || "__ungrouped";
    let group = groups.get(key);
    if (!group) {
      const info = meta.get(key);
      group = {
        key,
        order: info?.standard_order ?? 999,
        title: info?.standard_title || "",
        percentage: info?.percentage ?? null,
        answers: [],
      };
      groups.set(key, group);
    }
    group.answers.push(answer);
  }
  return [...groups.values()].sort((a, b) => a.order - b.order);
};

function Chevron({ open }) {
  return (
    <svg
      aria-hidden="true"
      className={`w-4 h-4 shrink-0 text-slate-400 transition-transform duration-200 ${
        open ? "rotate-180" : ""
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// A note added after submit. The write-up and the answers stay as they were
// sent; this goes below them with its own date, and the teacher reads it too.
function AddendumComposer({ uuid, onAdded, onCancel, t }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = text.trim();

  const add = async () => {
    if (busy || !trimmed) return;
    setBusy(true);
    try {
      const detail = await addObservationAddendum({ uuid, body: { text: trimmed } });
      toast.success(t("myObservations.addendumAdded"));
      onAdded(detail);
    } catch (err) {
      toast.error(formatApiError(err, t("myObservations.addendumFailed")));
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
      <label
        htmlFor={`addendum-${uuid}`}
        className="text-xs font-black text-emerald-800 uppercase tracking-wider"
      >
        {t("myObservations.addendumAdd")}
      </label>
      <p className="text-[11px] text-emerald-700/80 font-medium mt-0.5 mb-2">
        {t("myObservations.addendumHint")}
      </p>
      <textarea
        id={`addendum-${uuid}`}
        rows={4}
        maxLength={4000}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("myObservations.addendumPlaceholder")}
        className="w-full bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500 outline-none resize-y"
      />
      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={`px-4 py-2 rounded-xl text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 ${FOCUS_RING}`}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={add}
          disabled={busy || !trimmed}
          className={`px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 ${FOCUS_RING}`}
        >
          {t("myObservations.addendumSubmit")}
        </button>
      </div>
    </div>
  );
}

// Oldest first, each under its date and author: read top to bottom it is the
// record as it grew.
function Addenda({ addenda, t }) {
  if (!addenda.length) return null;
  return (
    <div>
      <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-1.5">
        {t("myObservations.addendaHeading")}
      </p>
      <ul className="space-y-2.5">
        {addenda.map((a) => (
          <li key={a.uuid} className="border-l-2 border-emerald-200 pl-3">
            <p className="text-[11px] font-bold text-slate-400">
              {formatShortDate(a.created_at)} · {fullName(a.author)}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {a.text}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
    </svg>
  );
}

// The write-up of one visit, fetched only when a row is opened — the list
// serializer carries no feedback and no answers, and pulling details for a
// whole page to render four collapsed rows would be most of a page load wasted.
//
// Nothing here is editable after submit. On the "conducted" tab the observer
// can append a dated note instead: what they did not get down during the lesson
// is usually written that evening, and the teacher has already read the rest.
function Record({ detail, segment, onDetailChanged, t }) {
  const [adding, setAdding] = useState(false);
  const shared = (detail.feedback?.shared_note || "").trim();
  const privateNote = (detail.feedback?.private_note || "").trim();
  const addenda = detail.addenda || [];
  const groups = groupAnswers(detail);
  const canAdd = segment === "conducted";
  const tally = (detail.answers || []).reduce(
    (acc, a) => {
      const v = verdictOf(a);
      acc[v === VERDICT.yes ? "yes" : v === VERDICT.no ? "no" : "none"] += 1;
      return acc;
    },
    { yes: 0, no: 0, none: 0 },
  );

  const addButton = canAdd && !adding && (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={() => setAdding(true)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors ${FOCUS_RING}`}
      >
        <PlusIcon />
        {t("myObservations.addendumAdd")}
      </button>
    </div>
  );

  const composer = adding && (
    <AddendumComposer
      uuid={detail.uuid}
      onAdded={(next) => {
        onDetailChanged(detail.uuid, next);
        setAdding(false);
      }}
      onCancel={() => setAdding(false)}
      t={t}
    />
  );

  if (!adding && !shared && !privateNote && addenda.length === 0 && groups.length === 0) {
    return (
      <div className="space-y-2 pt-1">
        {addButton}
        <p className="text-xs text-slate-400 font-medium py-2">
          {t("myObservations.noWriteUp")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-1">
      {addButton}

      {shared && (
        <div>
          <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-1">
            {segment === "conducted"
              ? t("myObservations.noteYouWrote")
              : t("myObservations.noteForYou")}
          </p>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {shared}
          </p>
        </div>
      )}

      {/* Only ever non-empty for the observer — the API returns null here for
          the teacher the visit was about. */}
      {privateNote && (
        <div>
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
            {t("myObservations.notePrivate")}
          </p>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
            {privateNote}
          </p>
        </div>
      )}

      <Addenda addenda={addenda} t={t} />
      {composer}

      {groups.length > 0 && (
        <div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              {t("myObservations.answersHeading")}
            </p>
            <p className="text-[11px] font-bold text-slate-400">
              {t("common.yes")} {tally.yes} · {t("common.no")} {tally.no} ·{" "}
              {t("common.notObserved")} {tally.none}
            </p>
          </div>

          <div className="space-y-2">
            {groups.map((group, groupIdx) => (
              <div
                key={group.key}
                className="rounded-xl border border-slate-200 bg-white overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 bg-slate-100/70 border-b border-slate-200">
                  <span className="text-[10px] font-black text-slate-500 tabular-nums shrink-0">
                    {group.order || groupIdx + 1}
                  </span>
                  <p className="text-[11px] font-bold text-slate-600 flex-1 min-w-[6rem]">
                    {group.title || t("myObservations.standardFallback")}
                  </p>
                  {/* One square per question — the shape of the standard before
                      a single row is read. */}
                  <span className="flex gap-1 shrink-0" aria-hidden="true">
                    {group.answers.map((a) => (
                      <span
                        key={a.uuid}
                        className={`w-2.5 h-2.5 rounded-[3px] ${verdictOf(a).dot}`}
                      />
                    ))}
                  </span>
                  {group.percentage != null && (
                    <span className="text-[11px] font-black text-slate-600 tabular-nums shrink-0">
                      {Math.round(Number(group.percentage))}%
                    </span>
                  )}
                </div>

                <ul className="divide-y divide-slate-100">
                  {group.answers.map((a, idx) => {
                    const verdict = verdictOf(a);
                    const note = (a.comment || "").trim();
                    return (
                      <li key={a.uuid} className="px-3 py-2.5">
                        <div className="flex items-start gap-3">
                          {/* The ordinal is a scanning spine, not content — on a
                              phone its column costs more than it gives. */}
                          <span className="hidden sm:block text-[10px] font-black text-slate-300 tabular-nums pt-0.5 shrink-0 w-7">
                            {group.order || groupIdx + 1}.{idx + 1}
                          </span>
                          <p className="text-[13px] text-slate-700 leading-snug min-w-0 flex-1">
                            {a.question_text_snapshot}
                          </p>
                          <span
                            className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md shrink-0 ${verdict.badge}`}
                          >
                            {a.selected_option_label_snapshot || t(verdict.labelKey)}
                          </span>
                        </div>
                        {/* Full row width, not the question's column: indented
                            under a badge it wrapped to three words a line. */}
                        {note && (
                          <p className="mt-1.5 sm:ml-10 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-600 leading-relaxed whitespace-pre-line">
                            {note}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyObservations() {
  const { t } = useTranslation();
  const [segment, setSegment] = useState("conducted");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  // Straight from the paginator: `next`/`previous` are the only honest answer to
  // "is there another page". Deriving it from count and the rows on screen gets
  // the LAST page wrong — a short final page makes page*rows.length < count, so
  // the button stays live and the click 404s.
  const [links, setLinks] = useState({ next: null, previous: null });
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [openUuid, setOpenUuid] = useState(null);
  const [details, setDetails] = useState({});
  const [detailError, setDetailError] = useState("");

  // Keyed on the whole segment, not on its role: "conducted" and "assigned" ask
  // the same role and differ only in status, so a role-keyed effect would leave
  // the finalized list sitting under the Assigned tab. The entries are module
  // constants, so this identity is stable across renders.
  const config = SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0];
  const todayISO = toLocalISODate(new Date());

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError("");
    getMyObservations({
      params: {
        page,
        role: config.role,
        status__in: config.statuses,
        ordering: config.ordering,
      },
      signal: controller.signal,
    })
      .then((data) => {
        setRows(data?.results || []);
        setCount(data?.count ?? 0);
        setLinks({ next: data?.next || null, previous: data?.previous || null });
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setRows([]);
        setCount(0);
        setLinks({ next: null, previous: null });
        setError(formatApiError(err, t("myObservations.loadFailed")));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [config, page, t]);

  const toggleRow = useCallback(
    (uuid) => {
      if (openUuid === uuid) {
        setOpenUuid(null);
        return;
      }
      setOpenUuid(uuid);
      setDetailError("");
      if (details[uuid]) return;
      getObservation({ uuid })
        .then((data) => setDetails((prev) => ({ ...prev, [uuid]: data })))
        .catch((err) =>
          setDetailError(formatApiError(err, t("myObservations.loadFailed"))),
        );
    },
    [openUuid, details, t],
  );

  // An addendum answers with the whole detail payload, so the cached copy is
  // replaced rather than patched.
  const handleDetailChanged = useCallback((uuid, detail) => {
    setDetails((prev) => ({ ...prev, [uuid]: detail }));
  }, []);

  const goToPage = (next) => {
    setPage(next);
    // The expanded row belongs to the page it was opened on.
    setOpenUuid(null);
  };

  const switchSegment = (key) => {
    setSegment(key);
    goToPage(1);
  };

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* No page heading here — BaseLayout's header bar already carries the
            title from PAGE_TITLES, and repeating it stacked the same words
            twice down the top of the page. The subtitle moved off
            `myObservations.subtitle` because that one promised "every FINISHED
            visit", and a third of this page is now visits nobody has run. */}
        <p className="text-sm text-slate-500 font-medium">
          {t("myObservations.subtitleAll")}
        </p>

        <div className="flex items-center gap-2 p-1.5 bg-indigo-50/50 rounded-[24px] w-fit max-w-full overflow-x-auto">
          {SEGMENTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => switchSegment(s.key)}
              aria-pressed={segment === s.key}
              className={`px-5 py-2.5 rounded-[18px] text-xs font-bold whitespace-nowrap transition-colors ${FOCUS_RING} ${
                segment === s.key
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 font-semibold">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-[32px] border-2 border-dashed border-slate-200 bg-white/60 py-16 px-6 text-center">
            <p className="text-sm font-black text-slate-700">
              {t(config.emptyKey)}
            </p>
            <p className="text-xs text-slate-500 font-medium mt-2 max-w-md mx-auto">
              {t(config.emptyBodyKey)}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              {t("myObservations.total", { n: count })}
            </p>
            <div className="space-y-3">
              {rows.map((row) => {
                const isOpen = openUuid === row.uuid;
                // Only the observed teacher's own segment carries a figure —
                // on "I ran this" the API masks it by design.
                const pct = row.percentage;
                // The booked day has passed and nobody has run the visit. This
                // is the whole reason the tab sorts oldest-first: an assignment
                // that quietly expires is the failure mode the tab exists for.
                const overdue =
                  segment === "assigned" &&
                  !!row.scheduled_date &&
                  row.scheduled_date < todayISO;
                return (
                  <div
                    key={row.uuid}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleRow(row.uuid)}
                      aria-expanded={isOpen}
                      className={`w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-slate-50 transition-colors ${FOCUS_RING}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-900 truncate">
                          {segment === "observed"
                            ? row.lesson_topic || t("myObservations.lessonFallback")
                            : fullName(row.observed_teacher) || "—"}
                        </p>
                        <p className="text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                          {[
                            // An assigned visit has no observed_at yet, so its
                            // date is the day it was booked for — the same day
                            // `overdue` above is measured against.
                            segment === "assigned"
                              ? row.scheduled_date &&
                                formatShortDate(row.scheduled_date)
                              : row.observed_at && formatShortDate(row.observed_at),
                            segment === "assigned" && row.period
                              ? t("upcoming.period", { period: row.period })
                              : null,
                            segment === "observed" ? null : row.lesson_topic,
                            row.group?.name,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      {/* Rose AND the word: the chip has to survive being read
                          by someone who cannot tell it from the slate one. */}
                      {overdue && (
                        <span className="shrink-0 inline-flex items-center rounded-md bg-rose-50 px-2.5 py-1 text-[11px] font-semibold leading-none text-rose-600">
                          {t("myObservations.overdue")}
                        </span>
                      )}
                      {pct != null && (
                        <span className="text-sm font-black text-slate-700 shrink-0">
                          {Math.round(Number(pct))}%
                        </span>
                      )}
                      <Chevron open={isOpen} />
                    </button>

                    {/* Outside the header button, not inside it: the row is
                        already one big <button> and nesting a link in it is
                        invalid. Shown without expanding, because expanding an
                        assigned visit only reveals that it has no write-up yet
                        — the answer to "so what do I do" is this link, and
                        making someone open an empty panel to find it is the
                        long way round the shortest path in the product.
                        A peer visit the colleague has not accepted gets the
                        Upcoming tab's "awaiting" chip instead — not theirs to
                        walk into yet, and a missing button alone says nothing. */}
                    {segment === "assigned" && awaitsPeerConfirmation(row) && (
                      <div className="px-5 pb-4 -mt-1">
                        <span
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold leading-none ${
                            displayStatusMeta(DISPLAY_STATUS.AWAITING).badge
                          }`}
                        >
                          {t(displayStatusMeta(DISPLAY_STATUS.AWAITING).labelKey)}
                        </span>
                      </div>
                    )}
                    {segment === "assigned" && !awaitsPeerConfirmation(row) && (
                      <div className="px-5 pb-4 -mt-1">
                        <Link
                          to={`/observation?visit=${row.uuid}`}
                          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${FOCUS_RING} ${
                            overdue
                              ? "bg-indigo-600 text-white hover:bg-indigo-700"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          {t("myObservations.conduct")}
                        </Link>
                      </div>
                    )}

                    {isOpen && (
                      <div className="px-5 pb-5 pt-1 border-t border-slate-100 bg-slate-50/40">
                        {details[row.uuid] ? (
                          <Record
                            detail={details[row.uuid]}
                            segment={segment}
                            onDetailChanged={handleDetailChanged}
                            t={t}
                          />
                        ) : detailError ? (
                          <p className="text-xs text-rose-600 font-semibold py-2">
                            {detailError}
                          </p>
                        ) : (
                          <Spinner size="sm" className="py-6" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {(links.next || links.previous) && (
              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  type="button"
                  disabled={!links.previous}
                  onClick={() => goToPage(page - 1)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`}
                >
                  {t("myObservations.prev")}
                </button>
                <span className="text-xs font-bold text-slate-400">{page}</span>
                <button
                  type="button"
                  disabled={!links.next}
                  onClick={() => goToPage(page + 1)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`}
                >
                  {t("myObservations.next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
